import { Banner, Button, Dialog } from '@cloudflare/kumo';
import { useState } from 'react';
import { useAppToast } from '../hooks/useAppToast';
import { ImportResult } from '../types';
import { apiService } from '../services/api';

interface CSVImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export function CSVImportModal({ isOpen, onClose, onSuccess }: CSVImportModalProps) {
  const toast = useAppToast();
  const [file, setFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (!selectedFile) return;

    if (selectedFile.type !== 'text/csv' && !selectedFile.name.endsWith('.csv')) {
      toast.error('Por favor, selecione um arquivo CSV válido');
      return;
    }
    setFile(selectedFile);
    setImportResult(null);
  };

  const handleImport = async () => {
    if (!file) { toast.error('Selecione um arquivo CSV'); return; }

    setIsUploading(true);
    try {
      const result = await apiService.importCSV(file);
      setImportResult(result);

      if (result.success) {
        toast.success(`Importação concluída! ${result.successfulImports} contatos importados.`);
        onSuccess();
      } else {
        toast.warning(`Importação parcial: ${result.successfulImports} sucessos, ${result.failedImports} erros.`);
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Erro ao importar arquivo');
    } finally {
      setIsUploading(false);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const blob = await apiService.downloadCSVTemplate();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'template-contatos.csv';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success('Template baixado com sucesso!');
    } catch {
      toast.error('Erro ao baixar template CSV');
    }
  };

  const handleClose = () => {
    setFile(null);
    setImportResult(null);
    onClose();
  };

  return (
    <Dialog.Root open={isOpen} onOpenChange={(o: boolean) => { if (!o) (handleClose)(); }}>
<Dialog size="base" className="p-6">


      <h3 className="text-lg font-semibold mb-1">Importar Contatos via CSV</h3>
      <div className="mb-6" style={{ color: 'var(--text-muted)' }}>Importe múltiplos contatos de uma só vez</div>

      <div className="flex flex-col gap-4">
        {/* Template download */}
        <div className="flex items-center justify-between p-4 rounded-md border border-[var(--link)] bg-[var(--tint-blue)]">
          <div>
            <div className="font-semibold" style={{ color: 'var(--text)' }}>Template CSV</div>
            <div style={{ color: 'var(--text)', fontSize: 12 }}>Baixe o template com o formato correto</div>
          </div>
          <Button variant="secondary" onClick={handleDownloadTemplate}>
            Baixar Template
          </Button>
        </div>

        {/* File upload */}
        <div>
          <div className="font-semibold mb-2" style={{ color: 'var(--text)' }}>Selecionar arquivo CSV</div>
          <label
            htmlFor="csv-file"
            className="flex flex-col items-center justify-center w-full p-6 border-2 border-dashed border-[var(--border-strong)] rounded-md hover:border-[var(--brand-text)] cursor-pointer transition-colors bg-[var(--surface-2)]"
          >
            <svg className="w-8 h-8 text-[var(--text-faint)] mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
            </svg>
            <div className="font-semibold" style={{ color: 'var(--text-muted)' }}>Clique para selecionar</div>
            <div style={{ color: 'var(--text-faint)', fontSize: 12, marginTop: 4 }}>Formato: .csv</div>
            <input id="csv-file" type="file" accept=".csv,text/csv" onChange={handleFileChange} className="hidden" />
          </label>

          {file && (
            <Banner variant="default" className="mt-2" description={<>
              {file.name}
            </>} />
          )}
        </div>

        {/* Instructions */}
        <div className="p-3 rounded-md border border-[var(--border)] bg-[var(--surface-2)]">
          <div className="font-semibold mb-1" style={{ color: 'var(--text)' }}>Instruções</div>
          <ul className="text-xs text-[var(--text-muted)] space-y-1">
            <li>• Colunas obrigatórias: <strong>nome</strong> e <strong>telefone</strong></li>
            <li>• Opcionais: email, observacoes, categoriaId</li>
            <li>• Use o template como referência</li>
          </ul>
        </div>

        {/* Result */}
        {importResult && (
          <Banner variant={importResult.success ? 'default' : 'alert'} description={<>
            Total: {importResult.totalRows} |
            Sucessos: {importResult.successfulImports} |
            Erros: {importResult.failedImports}
            {importResult.errors.length > 0 && (
              <details className="mt-2 cursor-pointer">
                <summary className="text-xs underline">Ver erros ({importResult.errors.length})</summary>
                <ul className="mt-1 text-xs space-y-0.5">
                  {importResult.errors.map((e, i) => <li key={i}>{e}</li>)}
                </ul>
              </details>
            )}
          </>} />
        )}

        <div className="flex gap-3 pt-2 justify-end">
          <Button variant="secondary" onClick={handleClose}>Fechar</Button>
          <Button variant="primary" onClick={handleImport} disabled={!file || isUploading} loading={isUploading}>
            Importar
          </Button>
        </div>
      </div>
    
</Dialog>
</Dialog.Root>
  );
}
